import React from "react";
import moment from "moment-timezone";
import { timeConversion } from "client-app-core";
import find from "lodash/find";
import { getTranslation } from "orion-components/i18n";
import convert from "convert-units";

const getUOM = (value, uomType) => {
	if (value === "imperial") {
		if (uomType === "distance") return "ft";
		if (uomType === "speed") return "mph";
	}
	if (value === "metric") {
		if (uomType === "distance") return "m";
		if (uomType === "speed") return "kph";
	}
	// Default return value to handle undefined cases
	return "";
};

const unitMappings = { kph: "km/h", mph: "m/h", m: "m", ft: "ft" };

const getConditions = (rule, collections, shapes) => {
	if (rule.conditions.length === 0) {
		return "";
	} else {
		let andString = "";
		const timeFormatPreference = rule.timeFormatPreference ? rule.timeFormatPreference : "12-hour";
		const { ruleConditionsById } = rule;

		// Render conditions portion of rule statement
		const conditions = rule.conditions.map((cond) => {
			if (!cond.isInComplete) {
				const conditionType = ruleConditionsById[rule.type][cond.type]?.conditionType ?? "";
				switch (conditionType) {
					case "time": {
						const startTime = moment.utc(cond.startTime, "h:mm A");
						const endTime = moment.utc(cond.endTime, "h:mm A");

						const timeFormat = timeFormatPreference === "24-hour" ? "H:mm z" : "h:mm A z";
						const startTimeTZ = timeConversion.convertToUserTime(startTime, timeFormat);
						const endTimeTZ = timeConversion.convertToUserTime(endTime, timeFormat);

						let finalString = "";

						if (cond.anyTimeOfDay) {
							finalString += "";
						} else {
							finalString += getTranslation(
								"global.ruleBuilder.conditionBuilder.betweenValues",
								"",
								startTimeTZ,
								endTimeTZ
							);
						}

						if (cond.weekdays.length === 7) {
							finalString += "";
						} else {
							if (finalString.length === 0) {
								finalString += getTranslation("global.ruleBuilder.conditionBuilder.itIs");
							}
							const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
							finalString += cond.weekdays
								.map(
									(index, i) =>
										(i === cond.weekdays.length && i > 1
											? getTranslation("global.ruleBuilder.conditionBuilder.or")
											: " ") +
										getTranslation(`global.ruleBuilder.conditionBuilder.${days[index]}`)
								)
								.join(",");
						}

						if (cond.indefinite) {
							finalString += "";
						} else {
							const startDate = timeConversion.convertToUserTime(cond.startDate, "ll");
							const endDate = timeConversion.convertToUserTime(cond.endDate, "ll");
							if (cond.weekdays.length && cond.weekdays.length !== 7) {
								finalString += getTranslation("global.ruleBuilder.conditionBuilder.and");
							}
							finalString +=
								startDate === endDate
									? getTranslation("global.ruleBuilder.conditionBuilder.dateIs", startDate)
									: getTranslation(
											"global.ruleBuilder.conditionBuilder.duringPeriod",
											"",
											startDate,
											endDate
									  );
						}

						finalString += "";
						andString = getTranslation("global.ruleBuilder.conditionBuilder.and");
						return finalString;
					}
					case "speed": {
						const conditions = [];
						if (cond.minSpeed) {
							const minSpeed = getTranslation(
								"global.ruleBuilder.conditionBuilder.travelingSlower",
								"",
								cond.minSpeed,
								cond.unit
							);
							conditions.push(minSpeed);
						}
						if (cond.maxSpeed) {
							const maxSpeed = getTranslation(
								"global.ruleBuilder.conditionBuilder.travelingFaster",
								"",
								cond.maxSpeed,
								cond.unit
							);
							conditions.push(maxSpeed);
						}
						andString = getTranslation("global.ruleBuilder.conditionBuilder.and");
						return conditions.join(getTranslation("global.ruleBuilder.conditionBuilder.or"));
					}

					case "in-collection": {
						const collection = find(collections, (col) => col.id === cond.id);
						if (!collection) {
							return "";
						}
						const collectionName = collection.name;
						andString = getTranslation("global.ruleBuilder.conditionBuilder.and");
						return getTranslation("global.ruleBuilder.conditionBuilder.inCollection", collectionName);
					}

					case "not-in-collection": {
						const collection = find(collections, (col) => col.id === cond.id);
						if (!collection) {
							return "";
						}
						const collectionName = collection.name;
						andString = getTranslation("global.ruleBuilder.conditionBuilder.and");
						return getTranslation("global.ruleBuilder.conditionBuilder.notInCollection", collectionName);
					}
					case "duration": {
						const duration = cond.duration;
						return duration === 1
							? getTranslation("global.ruleBuilder.conditionBuilder.longerThanMin", duration)
							: getTranslation("global.ruleBuilder.conditionBuilder.longerThanMins", duration);
					}
					case "direction": {
						const direction = getTranslation(
							`global.ruleBuilder.conditionBuilder.direction.${cond.direction}`
						);
						return getTranslation("global.ruleBuilder.conditionBuilder.heading", direction);
					}
					case "in-zone":
					case "not-in-zone": {
						andString = getTranslation("global.ruleBuilder.conditionBuilder.and");
						const shapeNames = [];
						for (const zoneId of cond.zones) {
							const shape = find(shapes, (shape) => shape.id === zoneId);
							if (shape) shapeNames.push(shape.entityData.properties.name);
							else shapeNames.push(zoneId);
						}

						let prefix;
						if (conditionType === "in-zone") {
							prefix =
								shapeNames.length > 1
									? getTranslation("global.ruleBuilder.conditionBuilder.isWithinZones")
									: getTranslation("global.ruleBuilder.conditionBuilder.isWithinZone");
						} else if (conditionType === "not-in-zone") {
							prefix =
								shapeNames.length > 1
									? getTranslation("global.ruleBuilder.conditionBuilder.isNotWithinZones")
									: getTranslation("global.ruleBuilder.conditionBuilder.isNotWithinZone");
						}
						return `${prefix} ${shapeNames.join(
							` ${getTranslation("global.ruleBuilder.conditionBuilder.or")} `
						)}`;
					}
					case "propertyComparison": {
						andString = getTranslation("global.ruleBuilder.conditionBuilder.and");
						const { property = "", propertyType } = cond;
						if (property !== "") {
							const { args = {} } = ruleConditionsById[rule.type]?.[cond.type] ?? {};
							const { operations = {}, statementLabel = "" } = args;
							let { inputValue = "" } = cond;
							if (cond.uomType === "distance" || cond.uomType === "speed") {
								let toUnit;
								if (cond.uomType === "distance") {
									toUnit = unitMappings[getUOM(rule.userUOM, cond.uomType)] ?? null;
								} else if (cond.uomType === "speed") {
									toUnit = unitMappings[getUOM(rule.userUOM, cond.uomType)] ?? null;
								}
								//For the create rule, the toUnit will be undefined. Therefore, set it to use the current UOM to avoid issues.
								toUnit = toUnit === undefined ? unitMappings[cond.uom] : toUnit;
								if (toUnit && cond.uomType !== toUnit) {
									inputValue = convert(inputValue).from(unitMappings[cond.uom]).to(toUnit);
								}
							}
							let ruleStatement = statementLabel;
							if (propertyType === "number") {
								ruleStatement =
									statementLabel +
									" " +
									operations?.[cond?.operation] +
									" " +
									parseFloat(inputValue).toFixed(2) +
									" " +
									getUOM(rule.userUOM, cond.uomType);
							} else {
								ruleStatement = `${statementLabel} ${operations?.[cond?.operation]} ${inputValue}`;
							}
							return ruleStatement;
						} else {
							return "";
						}
					}
					default:
						break;
				}
			}
		});

		return conditions.map((condition) => {
			if (!condition || condition?.isInComplete) {
				return null;
			}
			return (
				<div key={condition} className="rule-statement-container">
					<span className="and-string">{andString}</span>
					<span>{condition}</span>
				</div>
			);
		});
	}
};

export default getConditions;
